import React, { useEffect, useState } from 'react';
import { brainAPI } from '../lib/api';
import { Loader2, RefreshCw, Brain, Mail, CheckSquare, CalendarClock, Zap, TrendingUp, AlertTriangle, ChevronDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

const ICON_MAP = {
  decision: Brain,
  task_created: CheckSquare,
  followup_created: CalendarClock,
  email_drafted: Mail,
};

const COLOR_MAP = {
  decision: 'bg-violet-100 text-violet-600 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800',
  task_created: 'bg-emerald-100 text-emerald-600 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
  followup_created: 'bg-amber-100 text-amber-600 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  email_drafted: 'bg-blue-100 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
};

const LABEL_MAP = {
  decision: 'AI Decision',
  task_created: 'Task Created',
  followup_created: 'Follow-up Created',
  email_drafted: 'Email Drafted',
};

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function TimelineItem({ item }) {
  const Icon = ICON_MAP[item.type] || Zap;
  const colorClass = COLOR_MAP[item.type] || 'bg-muted text-muted-foreground border-border';
  const label = LABEL_MAP[item.type] || item.type;

  const actionLabel = item.action?.replace('brain:', '').replace(/_/g, ' ') || '';

  // Break long reasoning into readable bullet points
  const reasoningPoints = (item.reasoning || '')
    .split(/[.!]\s+|—\s*|\n/)
    .map(s => s.trim())
    .filter(s => s.length > 8)
    .slice(0, 3);

  return (
    <div className="flex gap-3 group" data-testid={`timeline-item-${item.type}`}>
      <div className="flex flex-col items-center">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center border shrink-0 ${colorClass}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="w-px flex-1 bg-border group-last:hidden" />
      </div>
      <div className="pb-4 min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-semibold text-foreground">{label}</span>
          {item.confidence > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
              {Math.round(item.confidence * 100)}% conf
            </span>
          )}
          <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{formatTimeAgo(item.created_at)}</span>
        </div>
        {actionLabel && (
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">{actionLabel}</p>
        )}
        {reasoningPoints.length > 1 ? (
          <div className="space-y-0.5">
            {reasoningPoints.map((point, idx) => (
              <div key={idx} className="flex items-start gap-1.5">
                <div className="w-1 h-1 rounded-full bg-muted-foreground/40 mt-1.5 shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">{point.replace(/\.$/, '')}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{item.reasoning}</p>
        )}
        {item.output_summary && (
          <p className="text-[10px] text-muted-foreground/70 mt-1 italic">{item.output_summary}</p>
        )}
        {item.metadata?.score != null && (
          <div className="flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3 text-violet-500" />
            <span className="text-[10px] font-medium text-violet-600">Score: {item.metadata.score}/100</span>
            {item.metadata.qualification && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                item.metadata.qualification === 'hot' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                item.metadata.qualification === 'warm' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' :
                item.metadata.qualification === 'cold' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                'bg-muted text-muted-foreground'
              }`}>{item.metadata.qualification}</span>
            )}
          </div>
        )}
        {item.metadata?.risk_flags?.length > 0 && (
          <div className="flex items-start gap-1 mt-1">
            <AlertTriangle className="w-3 h-3 text-orange-500 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              {item.metadata.risk_flags.slice(0, 2).map((flag, idx) => (
                <p key={idx} className="text-[10px] text-orange-600 dark:text-orange-400 leading-relaxed">{flag}</p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BrainActivityTimeline({ leadId }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const fetchTimeline = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await brainAPI.getTimeline(leadId);
      setTimeline(res.data?.timeline || []);
      setTotal(res.data?.total || 0);
    } catch {
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeline();
  }, [leadId]);

  const displayed = showAll ? timeline : timeline.slice(0, 5);

  return (
    <div className="border border-border rounded-lg mb-4 overflow-hidden" data-testid="brain-activity-timeline">
      <Accordion type="single" collapsible defaultValue="brain-timeline" className="w-full">
        <AccordionItem value="brain-timeline" className="border-none">
          <div className="flex items-center px-4 py-3 hover:bg-muted/50 rounded-t-lg">
            <AccordionTrigger className="flex-1 hover:no-underline p-0 [&>svg]:ml-auto">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-violet-500" />
                <span className="text-sm font-semibold">Brain Activity Timeline</span>
                {total > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-400 font-medium ml-1">
                    {total}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <button
              onClick={(e) => { e.stopPropagation(); fetchTimeline(); }}
              className="ml-2 p-1 rounded hover:bg-muted transition-colors"
              data-testid="timeline-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <AccordionContent className="pb-0">
            <div className="px-4 py-3">
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
                  <span className="ml-2 text-xs text-muted-foreground">Loading timeline...</span>
                </div>
              ) : timeline.length === 0 ? (
                <div className="text-center py-6" data-testid="timeline-empty">
                  <Brain className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No AI activity yet for this lead.</p>
                  <p className="text-[10px] text-muted-foreground/60 mt-1">The Brain will analyze this lead when events occur.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-0">
                    {displayed.map((item, i) => (
                      <TimelineItem key={`${item.type}-${item.created_at}-${i}`} item={item} />
                    ))}
                  </div>
                  {timeline.length > 5 && !showAll && (
                    <button
                      onClick={() => setShowAll(true)}
                      className="w-full text-center py-2 text-xs text-violet-500 hover:text-violet-700 flex items-center justify-center gap-1 transition-colors"
                      data-testid="timeline-show-all-btn"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                      Show all {timeline.length} activities
                    </button>
                  )}
                </>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
